/**
 * Grid-menu data with the polling contract from the integration spec §G / PLAN.md §2.5.
 *
 * Bedrock does **not** currently broadcast app-directory changes over WebSocket — app changes
 * land in the audit log only — so freshness is polling-driven by design:
 *   - `refetchInterval: 60_000`
 *   - `refetchOnWindowFocus` and `refetchOnReconnect`
 *   - plus explicit invalidation when the current user's roles / allowed_apps change
 *     (AuthContext.refreshPermissions does that).
 *
 * Do not wire up `app_registered` / `app_updated` WS handlers until Bedrock actually ships them.
 */

import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { fetchAppDirectory, fetchAppDirectoryDisplay } from '@/services/directory';

export const APP_DIRECTORY_QUERY_KEY = ['app-directory'] as const;

export function useAppDirectory() {
  const { isAuthenticated, user } = useAuth();
  // A break-glass session has no Bedrock identity; the switcher is hidden entirely.
  const enabled = isAuthenticated && Boolean(user?.bedrock_available);

  return useQuery({
    queryKey: APP_DIRECTORY_QUERY_KEY,
    queryFn: fetchAppDirectory,
    enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1,
  });
}

/** Bedrock's advisory tile/grid config. Failure is non-fatal — we fall back to the defaults. */
export function useAppDirectoryDisplay() {
  const { isAuthenticated, user } = useAuth();
  const enabled = isAuthenticated && Boolean(user?.bedrock_available);

  return useQuery({
    queryKey: ['app-directory', 'display'],
    queryFn: fetchAppDirectoryDisplay,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}
